/**
 * Orchestrator Agent
 * 
 * The top-level agent that coordinates tasks using tools and sub-agents.
 * This is the main entry point for user conversations in the taskpane.
 */

import { ToolDescription } from "../base/toolDescription";
import { AgentBaseClass } from "../base";
import { ToolBase } from "../base/toolBase";
import type { ToolPreflightContext, ToolPreflightResult } from "../base/agentBaseClass";
import { SubmitResultsParams } from "../base/submitResults.schema";
import { ChatMessage } from "../../globals/llm/baseAdapter";
import { ThreadId, ResultItem, ResolvedContextItem, CancellationToken, AgentCallbacks } from "../../core/types";
import { ReadSlideTool } from "./tools/readSlide";
import { GetChartPointPositionsTool } from "./tools/getChartPointPositions";
import { ReorderOrDeleteSlidesTool } from "./tools/reorderOrDeleteSlides";
import { GetRelevantExcelFilesTool } from "./tools/getRelevantExcelFiles";
// DISABLED: artifact insertion now routes through writeSlide WriteSlideML flow.
// Keep import commented for rollback.
// import { InsertArtifactsTool } from "./tools/insertArtifacts";
import { GenerateBlueprintTool } from "./tools/generateBlueprint";
import { WriteSlideTool } from "./tools/writeSlide";
import { AddSlidesTool } from "./tools/addSlides";
import { CurrentPresentationSearchTool } from "./tools/currentPresentationSearch";
import { UserInterviewTool } from "../tools/userInterview";
import { PresentationLibrarySearchAgent } from "../search";
import {
    AlphaSenseAgent,
    ExternalFileSearchAgent,
    ImageSearchAgent,
    createPromptGetTool,
    createPromptSearchTool,
    PPT_PROMPT_SECTIONS,
} from "@carousel-office/agent-runtime";
import { LinkedExcelEditorAgent } from "../linkedExcelEditor";
import { OrchestratorParamsSchema, type OrchestratorParams, type OrchestratorResponse } from "./orchestrator.schema";
import { getDeckRegistry } from "../../../services/tracking/deckRegistry";
import GlobalStateManager from "../../globals/global-state/globalStateManager";
import { getCurrentPresentationSlideExclusions } from "../../globals/global-state/searchHistory";

// New standardized prompt builder
import { buildOrchestratorSystemPrompt } from "./prompt";

// ═══════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR AGENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Main orchestrator agent that coordinates user tasks
 * 
 * The orchestrator:
 * - Receives user messages from the taskpane
 * - Determines which tools/sub-agents to use
 * - **Actually executes tools** via the tool loop
 * - Streams responses back to the UI
 * - Manages the main conversation thread
 * 
 * Uses the base class execute() method with customized hooks:
 * - buildInitialMessages(): Builds user message from params
 * - onToolResult(): Tracks executed tool calls
 * - buildFinalResult(): Returns OrchestratorResponse format
 * 
 * @example
 * const orchestrator = new LivePowerPointOrchestrator();
 * const result = await orchestrator.execute(
 *     { message: "Add a new slide with quarterly revenue data" },
 *     { onChunk: (chunk) => updateUI(chunk) }
 * );
 */
class LivePowerPointOrchestrator extends AgentBaseClass<OrchestratorParams, OrchestratorResponse> {
    
    /** Track tool calls for response */
    private executedToolCalls: Array<{ name: string; result: unknown }> = [];

    // Agent type for task tracking
    readonly agentType = 'livePowerPointOrchestrator';

    // maxResponseTokens: inherits 0 from base → adapter uses model maximum
    
    constructor() {
        super();
        this.name = "livePowerPointOrchestrator";
        
        // Use base class parameter validation
        this.paramsSchema = OrchestratorParamsSchema;
        
        // Enable thread management for conversation history
        this.useTaskTracking = true;
        this.useThreadManagement = true;
        // Enable multimodal/user-provided context (e.g., asset_* image IDs from chat composer).
        this.useContextResolution = true;
        
        this.toolCalls = [
            // Direct tools
            new ReadSlideTool(),
            new GetChartPointPositionsTool(),
            new WriteSlideTool(),  // WriteSlideML: edit slides via ReadSlideML search/replace
            new AddSlidesTool(),  // Add/copy slides without buildFromScratch
            new ReorderOrDeleteSlidesTool(),
            new GetRelevantExcelFilesTool(),
            new CurrentPresentationSearchTool(),
            // Prompt environment tools (PowerPoint playbook sections).
            createPromptGetTool(PPT_PROMPT_SECTIONS),
            createPromptSearchTool(PPT_PROMPT_SECTIONS),
            // DISABLED: artifact insertion now routes through writeSlide WriteSlideML flow.
            // new InsertArtifactsTool(),  // Insert artifacts (Excel, images, shapes) into PowerPoint
            new GenerateBlueprintTool(),
            new UserInterviewTool(),
            // Sub-agents (Agent-as-Tool pattern)
            new AlphaSenseAgent(),
            new ExternalFileSearchAgent(),
            new LinkedExcelEditorAgent({ submitToolkitMode: "override" }),
            // Search agents (Agent-as-Tool pattern)
            new PresentationLibrarySearchAgent(),
            new ImageSearchAgent(),
        ];
        this.description = new ToolDescription(
            "livePowerPointOrchestrator",
            "Orchestrates tasks by coordinating multiple tools and agents. Acts as the main coordinator for complex workflows.",
            OrchestratorParamsSchema
        );
        
        // Default to streaming mode
        this.stream = true;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // HOOKS - Customize base class behavior
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Generate the system prompt for the orchestrator
     * 
     * Uses the standardized SystemPromptBuilder with 5 sections:
     * 1. Intro - Agent identity and goals
     * 2. Tools - Available tools with usage guidance
     * 3. Suggestions - Best practices and workflows
     * 4. Context Note - How context IDs work
     * 5. Evaluation - Performance criteria and reminders
     */
    public generateSystemPrompt(_resolvedContext?: ResolvedContextItem[]): string {
// Use the new standardized prompt builder
        return buildOrchestratorSystemPrompt({
            tools: this.toolCalls,
            currentdocumentId: this.currentdocumentId,
        });
    }
    
    /*
     * ═══════════════════════════════════════════════════════════════════════════
     * @DEPRECATED - OLD SYSTEM PROMPT (marked for deletion)
     * 
     * The old inline system prompt has been moved to:
     *   packages/ppt-addin/src/main/agents/orchestrator/prompt/
     * 
     * Delete this comment block after confirming the new prompt works correctly.
     * ═══════════════════════════════════════════════════════════════════════════
     */

    /**
     * Build initial messages from the user's message parameter
     */
    protected buildInitialMessages(params: OrchestratorParams, resolvedContext?: ResolvedContextItem[]): ChatMessage[] {
        if (resolvedContext && resolvedContext.length > 0) {
            const contextParts = this.buildContextContentParts(resolvedContext);
            return [{
                role: 'user' as const,
                content: [
                    { type: 'text', text: params.message },
                    ...(contextParts.length > 0
                        ? [{ type: 'text' as const, text: '\n\nAttached visual context:' }, ...contextParts]
                        : []),
                ],
            }];
        }

        return [{
            role: 'user' as const,
            content: params.message,
        }];
    }

    /**
     * Hook called before execution starts - reset tool call tracking and set up sub-agents
     */
    protected async beforeExecute(_context: {
        params: OrchestratorParams;
        threadId: ThreadId;
        taskId: string;
    }): Promise<void> {
        // Reset tool calls tracking for this execution
        this.executedToolCalls = [];
        
        // Set up deck registry for PresentationLibrarySearchAgent
        await this.setupDeckRegistryForSearchAgent();
    }
    
    /**
     * Set up the deck registry on the PresentationLibrarySearchAgent tool
     */
    private async setupDeckRegistryForSearchAgent(): Promise<void> {
        try {
            // Find the PresentationLibrarySearchAgent in our tools
            const searchAgent = this.toolCalls.find(
                tool => tool.name === 'presentationLibrarySearchAgent'
            ) as PresentationLibrarySearchAgent | undefined;
            
            if (!searchAgent) {
                console.log('[Orchestrator] PresentationLibrarySearchAgent not found in tools');
                return;
            }
            
            // Get deck registry and refresh it
            const deckRegistry = getDeckRegistry();
            await deckRegistry.initialize();
            
            // Build the registry map for the agent
            const registryMap = new Map<number, { id: string; title: string; slidesReady: number; totalSlides: number }>();
            const allDecks = deckRegistry.getAllDecks();
            
            for (let i = 0; i < allDecks.length; i++) {
                const deck = allDecks[i];
                registryMap.set(i, {
                    id: deck.id,
                    title: deck.title,
                    slidesReady: deck.slidesReady,
                    totalSlides: deck.totalSlides,
                });
            }
            
            // ARCHITECTURE FIX: Use the anchored deck ID from the conversation, not the live one.
            // The live deckRegistry.getCurrentdocumentId() could change if user switches presentations,
            // but a conversation should stay anchored to the presentation it was spawned in.
            // this.currentdocumentId is set by AgentBaseClass.execute() from the conversation context.
            const currentdocumentId = this.currentdocumentId || deckRegistry.getCurrentdocumentId();

            // Set anchored current deck on dedicated currentPresentationSearch tool.
            const currentPresentationSearchTool = this.toolCalls.find(
                tool => tool.name === 'currentPresentationSearch'
            ) as CurrentPresentationSearchTool | undefined;
            if (currentPresentationSearchTool) {
                currentPresentationSearchTool.setCurrentdocumentId(currentdocumentId);
            }
            
            // Set the registry on the agent
            searchAgent.setDeckRegistry(registryMap, currentdocumentId);
            
            // Log for debugging
            console.group('[Orchestrator] DECK REGISTRY SET UP FOR SEARCH AGENT');
            console.log('Current Deck (anchored):', currentdocumentId || 'none');
            console.log('Available Decks:', Array.from(registryMap.entries()).map(([idx, info]) => ({
                index: idx,
                title: info.title,
                ready: `${info.slidesReady}/${info.totalSlides}`,
                id: info.id,
                status: info.slidesReady === info.totalSlides ? '✓' : 
                        info.slidesReady > 0 ? '◐' : '⏳',
                isCurrent: info.id === currentdocumentId
            })));
            console.groupEnd();
            
        } catch (error) {
            console.warn('[Orchestrator] Failed to set up deck registry:', error);
        }
    }

    /**
     * Hook called when a tool returns a result - track for response
     */
    protected onToolResult(toolName: string, result: unknown): void {
        this.executedToolCalls.push({ name: toolName, result });

        const conversationId = this.currentConversationId;
        if (!conversationId) {
            return;
        }

        if (toolName === "currentPresentationSearch" && result && typeof result === "object") {
            const payload = result as Record<string, unknown>;
            const results = Array.isArray(payload.results) ? payload.results : [];
            const documentId = typeof payload.documentId === "string" ? payload.documentId : this.currentdocumentId;
            GlobalStateManager.getInstance().recordCurrentPresentationSearchResults(
                conversationId,
                documentId,
                results as Array<{ slideId?: unknown; resultId?: unknown; presentationId?: unknown }>
            );
        }
    }

    /**
     * Inject current-presentation exclusion IDs before currentPresentationSearch executes.
     */
    protected override async beforeToolExecute(context: ToolPreflightContext): Promise<ToolPreflightResult> {
        if (context.toolCall.name !== "currentPresentationSearch") {
            return { action: "continue" };
        }

        if (!context.args || typeof context.args !== "object") {
            return { action: "continue" };
        }

        const args = context.args as Record<string, unknown>;
        if (args.excludeAllPrevious !== true) {
            return { action: "continue" };
        }

        const conversationId = this.currentConversationId;
        if (!conversationId) {
            return { action: "continue" };
        }

        const documentId = this.currentdocumentId;
        const history = GlobalStateManager.getInstance().getConversationSearchHistory(conversationId);
        const slidesToExcludeFromHistory = getCurrentPresentationSlideExclusions(history, documentId);
        if (slidesToExcludeFromHistory.length === 0) {
            return { action: "continue" };
        }

        const existingSlides = Array.isArray(args.slidesToExclude) ? args.slidesToExclude.map(String) : [];
        args.slidesToExclude = [...new Set([...existingSlides, ...slidesToExcludeFromHistory])];

        console.log(
            `[Orchestrator] Injected ${slidesToExcludeFromHistory.length} currentPresentationSearch exclusions ` +
            `for deck ${documentId || "unknown"}`
        );

        return { action: "continue" };
    }
    
    /**
     * Override reminder options to include deck registry context.
     * 
     * The orchestrator needs deck registry so it can see all available
     * presentations and their slide descriptions when coordinating tasks.
     * It also needs presentation overview with styles and layouts for
     * consistent styling guidance.
     */
    protected override getReminderOptions(): {
        includePresentation?: boolean;
        includeCustomInstructions?: boolean;
        includeSecurityWarning?: boolean;
        includeDeckRegistry?: boolean;
        includeExternalFiles?: boolean;
        includePresentationOverview?: boolean;
        anchoreddocumentId?: string;
    } {
        console.log('[Orchestrator] 🔔 getReminderOptions called - INCLUDING DECK REGISTRY + EXTERNAL FILES + PRESENTATION OVERVIEW');
        return {
            includePresentation: true,
            includeSecurityWarning: true,
            // Keep live-editing turns aligned with user instructions from reminder pipeline.
            includeCustomInstructions: true,
            includeDeckRegistry: true,  // ✅ ENABLED for orchestrator
            includeExternalFiles: true,  // ✅ ENABLED - shows indexed RAG files
            includePresentationOverview: true,  // ✅ ENABLED - styles and layouts for consistency
            // ARCHITECTURE: Use the conversation's anchored documentId so the deck registry
            // shows the correct "current" deck, not the live UI state.
            anchoreddocumentId: this.currentdocumentId,
        };
    }

    /**
     * Build the orchestrator-specific response format
     */
    protected buildFinalResult(
        _results: ResultItem<unknown>[],
        _threadId: ThreadId,
        _tokensUsed?: number,
        _summary?: string
    ): OrchestratorResponse {
        // Get the content from the last loop result
        const content = this.lastLoopResult?.content || '';
        const message = this.lastParams?.message || '';
        
        return { 
            response: { 
                content,
                message,
                toolCalls: this.executedToolCalls.length > 0 ? this.executedToolCalls : undefined,
            } 
        };
    }

    /**
     * Process submitted results (required abstract method).
     * 
     * Orchestrator does not use the Submit Results Pattern - it streams
     * responses directly to the user via processResults instead.
     * This method will not be called in normal operation.
     */
    protected async processSubmittedResults(
        _submittedResults: SubmitResultsParams,
        _params: OrchestratorParams,
        _context: {
            params: OrchestratorParams;
            threadId: ThreadId;
            taskId: string;
            cancellationToken: CancellationToken;
            resolvedContext: ResolvedContextItem[];
            conversationId: string;
            documentId: string;
            callbacks?: AgentCallbacks;
        }
    ): Promise<ResultItem<unknown>[]> {
        // Orchestrator doesn't use Submit Results Pattern - this won't be called
        throw new Error('Orchestrator does not use Submit Results Pattern');
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Set streaming mode
     */
    public setStreaming(enabled: boolean): void {
        this.stream = enabled;
    }

    /**
     * Check if streaming is enabled
     */
    public isStreaming(): boolean {
        return this.stream;
    }
}

export default LivePowerPointOrchestrator;
export { LivePowerPointOrchestrator, OrchestratorParamsSchema };
export type { OrchestratorParams, OrchestratorResponse };
